import * as React from 'react';

import {
  AccountType,
  enumAccountType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Checkbox,
  ContentCard,
  ContentInfo,
  ContentSection,
  FullName,
  LocalizedPhone,
  MultiSelect,
  TextField,
  TextArea,
  ensureValue,
  cn,
  PhoneField,
  Loading,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import identity from 'lodash/identity';
import { LucideCheck, LucidePencil, LucideSend } from 'lucide-react';

import { useInviteCustomerMutation } from '../../_graphql/mutationCustomerInvite';
import { AccountFormState } from '../../_state/_types';
import * as accountDetailsState from '../../_state/accountDetails.state';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AccountRelationshipBadges, AccountTypeBadge } from './AccountBadges';
import { AccountCounterpartyGroups } from './AccountCounterpartyGroups';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import {
  SelectableUser,
  useUsersQuery,
} from '~/shared/components/select-fields/SelectUser';
import { keymap } from '~/shared/keymap';

export const AccountTabDetails = () => {
  const t = useTranslations();
  const toast = useToast();
  const account = useAtomValue(accountDetailsState.$selectedAccount);
  const canEditAccount = useAtomValue(accountEditingState.$canEditAccount);
  const updateAccountDetails = useSetAtom(
    accountEditingState.$updateRemoteAccount,
  );
  const inviteCustomer = useInviteCustomerMutation();

  const { data: users } = useUsersQuery();

  const [isEditing, setIsEditing] = React.useState(false);
  const [accountForm, setAccountForm] = React.useState<AccountFormState>({
    accountManagerIds: [],
  });

  const userOptions = React.useMemo(
    () =>
      (users ?? []).map((user: SelectableUser) => ({
        key: user.id,
        label: [user.firstName, user.lastName].filter(Boolean).join(' '),
      })),
    [users],
  );

  const handleStartEditing = () => {
    if (account) {
      setAccountForm({
        name: account.name,
        email: account.email,
        phone: account.phone,
        website: account.website,
        type: account.type,
        accountManagerIds:
          account.accountManagerIds ??
          account.accountManagers?.map((m) => m.id) ??
          [],
        relationship: account.relationship
          ? {
              isCustomer: account.relationship.isCustomer,
              isSupplier: account.relationship.isSupplier,
              isCarrier:
                (account.relationship as { isCarrier?: boolean }).isCarrier ??
                false,
            }
          : undefined,
        notes: account.notes,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setAccountForm({ accountManagerIds: [] });
  };

  const handleSaveEditing = async () => {
    if (account) {
      await updateAccountDetails({
        id: account.id,
        name: accountForm.name,
        email: accountForm.email,
        phone: accountForm.phone,
        website: accountForm.website,
        type: accountForm.type,
        accountManagerIds: accountForm.accountManagerIds,
        relationship: accountForm.relationship,
        notes: accountForm.notes,
      });
      setIsEditing(false);
    }
  };

  const handleFieldChange =
    <K extends keyof AccountFormState>(field: K) =>
    (value: AccountFormState[K]) => {
      setAccountForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleRelationshipChange =
    (field: 'isCustomer' | 'isSupplier' | 'isCarrier') =>
    (checked: boolean) => {
      setAccountForm((prev) => ({
        ...prev,
        relationship: {
          isCustomer: prev.relationship?.isCustomer ?? false,
          isSupplier: prev.relationship?.isSupplier ?? false,
          isCarrier: prev.relationship?.isCarrier ?? false,
          [field]: checked,
        },
      }));
    };

  const handleInviteCustomer = async () => {
    if (!account?.id) return;

    try {
      const result = await inviteCustomer.mutateAsync({
        accountId: account.id,
      });

      toast.success(
        t.accountsView.details.inviteSentSuccess(
          result.crmCustomerInvite.email,
        ),
      );
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '';

      if (
        errorMessage.includes('already linked') ||
        errorMessage.includes('already been sent')
      ) {
        toast.error(t.accountsView.details.inviteAlreadyLinked);
      } else if (errorMessage.includes('email is required')) {
        toast.error(t.accountsView.details.inviteNoEmail);
      } else {
        toast.error(t.accountsView.details.inviteFailed);
      }

      console.error('Error inviting customer:', error);
    }
  };

  const canSubmitForm = !!(
    accountForm.name &&
    accountForm.type &&
    accountForm.relationship
  );

  const canInviteCustomer = !!(account?.canBeInvited && !isEditing);

  return (
    <div className={cn('@AccountTabDetails', 'flex h-full flex-col gap-3 p-5')}>
      <Loading isLoading={!account}>
        <ContentCard>
          <ContentSection
            title={
              <div className={'flex items-center justify-between'}>
                <div>{t.accountsView.form.sections.accountDetails}</div>
                <div
                  className={cn(
                    'flex items-center gap-6',
                    isEditing && 'pointer-events-none opacity-0',
                  )}
                >
                  {canInviteCustomer && (
                    <Button
                      variant="link"
                      className="px-0 text-sm font-medium"
                      preventFocus
                      icon={<LucideSend />}
                      isLoading={inviteCustomer.isPending}
                      onClick={handleInviteCustomer}
                    >
                      {t.accountsView.details.inviteCustomer}
                    </Button>
                  )}
                  {canEditAccount && (
                    <Button
                      variant="link"
                      className="px-0 text-sm font-medium"
                      preventFocus
                      icon={<LucidePencil />}
                      onClick={handleStartEditing}
                    >
                      {t.accountsView.details.edit}
                    </Button>
                  )}
                </div>
              </div>
            }
          >
            {isEditing && canEditAccount ? (
              <div className="grid grid-cols-3 gap-4">
                <TextField
                  label={t.accountsView.form.fields.name}
                  placeholder={t.accountsView.form.fields.name}
                  autoFocus
                  value={accountForm.name}
                  onChange={handleFieldChange('name')}
                />
                <TextField
                  label={t.accountsView.form.fields.email}
                  type="email"
                  placeholder={t.accountsView.form.placeholders.email}
                  value={accountForm.email}
                  onChange={handleFieldChange('email')}
                />
                <PhoneField
                  label={t.accountsView.form.fields.phone}
                  placeholder={t.accountsView.form.placeholders.phone}
                  value={accountForm.phone}
                  onChange={handleFieldChange('phone')}
                />
                <TextField
                  label={t.accountsView.form.fields.website}
                  type="url"
                  placeholder={t.accountsView.form.placeholders.website}
                  value={accountForm.website}
                  onChange={handleFieldChange('website')}
                />
                <SelectEnum<AccountType>
                  enumValue={enumAccountType}
                  label={t.accountsView.form.fields.type}
                  value={accountForm.type}
                  onChange={handleFieldChange('type')}
                />
                <MultiSelect
                  label={t.accountsView.form.fields.accountManagers}
                  options={userOptions}
                  selectedKeys={accountForm.accountManagerIds}
                  onSelectionChange={handleFieldChange('accountManagerIds')}
                  placeholder={t.accountsView.form.placeholders.accountManagers}
                />
                <div className="col-span-3">
                  <label className="mb-2 block text-sm font-medium text-neutral/70">
                    {t.accountsView.form.fields.relationship}
                  </label>
                  <div className="flex gap-4">
                    <Checkbox
                      isSelected={accountForm.relationship?.isCustomer}
                      onChange={handleRelationshipChange('isCustomer')}
                    >
                      {t.accountsView.relationship.customer}
                    </Checkbox>
                    <Checkbox
                      isSelected={accountForm.relationship?.isSupplier}
                      onChange={handleRelationshipChange('isSupplier')}
                    >
                      {t.accountsView.relationship.supplier}
                    </Checkbox>
                    <Checkbox
                      isSelected={accountForm.relationship?.isCarrier}
                      onChange={handleRelationshipChange('isCarrier')}
                    >
                      {t.accountsView.relationship.carrier}
                    </Checkbox>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-5">
                <ContentInfo
                  label={t.accountsView.form.fields.name}
                  value={account?.name}
                >
                  {identity}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.email}
                  value={account?.email}
                >
                  {identity}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.phone}
                  value={account?.phone}
                >
                  {(phone) => <LocalizedPhone children={phone} />}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.website}
                  value={account?.website}
                >
                  {(website) => (
                    <a
                      className="text-primary hover:underline"
                      target="_blank"
                      href={ensureValue(website)}
                      rel="noreferrer"
                    >
                      {website}
                    </a>
                  )}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.type}
                  value={account?.type}
                >
                  {(type) => <AccountTypeBadge type={type} />}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.accountManagers}
                  value={
                    account?.accountManagers?.length
                      ? account.accountManagers
                      : undefined
                  }
                >
                  {(managers) => (
                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                      {managers.map((manager) => (
                        <span key={manager.id}>
                          <FullName person={manager} />
                        </span>
                      ))}
                    </div>
                  )}
                </ContentInfo>
                <ContentInfo
                  label={t.accountsView.form.fields.relationship}
                  value={account?.relationship}
                >
                  {(relationship) => (
                    <AccountRelationshipBadges relationship={relationship} />
                  )}
                </ContentInfo>
              </div>
            )}
          </ContentSection>
          <ContentSection>
            {isEditing && canEditAccount ? (
              <TextArea
                label={t.accountsView.form.fields.notes}
                placeholder={t.accountsView.form.placeholders.notes}
                rows={6}
                value={accountForm.notes || undefined}
                onChange={handleFieldChange('notes')}
              />
            ) : (
              <ContentInfo
                label={t.accountsView.form.fields.notes}
                value={account?.notes}
                className="max-w-[600px]"
              >
                {(notes) => <div className="whitespace-pre-wrap">{notes}</div>}
              </ContentInfo>
            )}
          </ContentSection>
          {isEditing && (
            <div className="col-span-3 mt-3 flex items-end justify-end gap-2 border-t border-neutral/10 pt-5">
              <Button
                preventFocus
                variant="outline"
                color="neutral"
                className={'border-neutral/20'}
                onClick={handleCancelEditing}
              >
                {t.accountsView.dialog.actions.cancel}
              </Button>
              <Button
                icon={<LucideCheck />}
                color="primary"
                variant="outline"
                preventFocus
                isLoading={false}
                isDisabled={!canSubmitForm}
                onClick={handleSaveEditing}
                keyboardShortcut={keymap.save}
              >
                {t.accountsView.dialog.actions.save}
              </Button>
            </div>
          )}
        </ContentCard>
        <AccountCounterpartyGroups />
      </Loading>
    </div>
  );
};
